import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { API } from "@/App";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import {
  PoundSterling, TrendingUp, Trophy, Users, Target, 
  Calendar, ArrowUp, ArrowDown, Home, Briefcase, 
  Phone, FileText, Truck, Crown, Star, Eye, ClipboardCheck,
  Handshake, CheckCircle2, BarChart3, Percent
} from "lucide-react";

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];

const QUARTERS = [
  { value: 1, label: "Q1 (Jan-Mar)" },
  { value: 2, label: "Q2 (Apr-Jun)" },
  { value: 3, label: "Q3 (Jul-Sep)" },
  { value: 4, label: "Q4 (Oct-Dec)" }
];

const PERIODS = [
  { value: "day", label: "Today" },
  { value: "week", label: "This Week" },
  { value: "month", label: "This Month" },
  { value: "quarter", label: "This Quarter" },
  { value: "year", label: "This Year" }
];

// Performance metrics helper component - moved outside to avoid re-creation on each render
const MetricCard = ({ icon: Icon, label, value, color = "text-slate-600" }) => (
  <div className="p-4 bg-white rounded-lg border">
    <div className="flex items-center gap-3">
      <div className={`p-2 rounded-lg ${color.replace('text-', 'bg-').replace('600', '100')}`}>
        <Icon className={`h-5 w-5 ${color}`} />
      </div>
      <div>
        <div className="text-2xl font-bold text-slate-900">{value}</div>
        <div className="text-sm text-slate-500">{label}</div>
      </div>
    </div>
  </div>
);

const ConversionCard = ({ label, value, icon: Icon }) => (
  <div className="p-3 bg-slate-50 rounded-lg text-center">
    <div className="flex items-center justify-center gap-1 mb-1">
      <Icon className="h-4 w-4 text-slate-400" />
      <span className="text-2xl font-bold text-slate-900">{value}%</span>
    </div>
    <div className="text-xs text-slate-500">{label}</div>
  </div>
);

export default function CommissionDashboard() {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const userStr = localStorage.getItem("user");
  const user = userStr ? JSON.parse(userStr) : null;
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(user?.role === "director" || user?.role === "admin" ? "team-overview" : "commission"); // Directors see team overview first
  const [myCommission, setMyCommission] = useState(null);
  const [allCommissions, setAllCommissions] = useState(null);
  const [myPerformance, setMyPerformance] = useState(null);
  const [allPerformance, setAllPerformance] = useState(null);
  const [teamOverview, setTeamOverview] = useState(null);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedQuarter, setSelectedQuarter] = useState(Math.ceil((new Date().getMonth() + 1) / 3));
  const [selectedPeriod, setSelectedPeriod] = useState("month");
  
  // Check if user is Sales Progressor (Tracey)
  const isSalesProgressor = user?.title?.toLowerCase().includes('progressor') || 
                            user?.email === 'tracey@goodacresresidential.co.uk';
  
  const isDirector = user?.role === "director" || user?.role === "admin";
  
  const fetchAllData = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch my commission
      const myCommRes = await fetch(
        `${API}/staff/commission/my?year=${selectedYear}&month=${selectedMonth}`,
        { headers }
      );
      if (myCommRes.ok) {
        const data = await myCommRes.json();
        setMyCommission(data);
      }
      
      // Fetch my performance
      const myPerfRes = await fetch(
        `${API}/staff/performance/my?period=${selectedPeriod}&year=${selectedYear}&month=${selectedMonth}`,
        { headers }
      );
      if (myPerfRes.ok) {
        const data = await myPerfRes.json();
        setMyPerformance(data);
      }
      
      // Fetch all data if director
      if (isDirector) {
        const allCommRes = await fetch(
          `${API}/staff/commission/all?year=${selectedYear}&quarter=${selectedQuarter}`,
          { headers }
        );
        if (allCommRes.ok) {
          const data = await allCommRes.json();
          setAllCommissions(data);
        }
        
        const allPerfRes = await fetch(
          `${API}/staff/performance/all?period=${selectedPeriod}&year=${selectedYear}&month=${selectedMonth}`,
          { headers }
        );
        if (allPerfRes.ok) {
          const data = await allPerfRes.json();
          setAllPerformance(data);
        }
        
        // Fetch team overview for director
        const teamRes = await fetch(
          `${API}/staff/team-overview?year=${selectedYear}&month=${selectedMonth}`,
          { headers }
        );
        if (teamRes.ok) {
          const data = await teamRes.json();
          setTeamOverview(data);
        }
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  }, [selectedYear, selectedMonth, selectedQuarter, selectedPeriod, isDirector, headers]);
  
  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);
  
  const fetchCommissionData = async () => {
    setLoading(true);
    try {
      // Fetch my commission
      const myRes = await fetch(
        `${API}/staff/commission/my?year=${selectedYear}&month=${selectedMonth}`,
        { headers }
      );
      if (myRes.ok) {
        const data = await myRes.json();
        setMyCommission(data);
      }
      
      // Fetch all commissions if director
      if (isDirector) {
        const allRes = await fetch(
          `${API}/staff/commission/all?year=${selectedYear}&quarter=${selectedQuarter}`,
          { headers }
        );
        if (allRes.ok) {
          const data = await allRes.json();
          setAllCommissions(data);
        }
      }
    } catch (error) {
      console.error("Error fetching commission:", error);
      toast.error("Failed to load commission data");
    } finally {
      setLoading(false);
    }
  };
  
  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return "—";
    return `£${amount.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };
  
  const getProgressToNextTier = () => {
    if (!allCommissions?.summary) return { progress: 0, target: 120000, current: 0 };
    
    const current = allCommissions.summary.total_exchanged_fees;
    let target = 120000;
    let tierName = "£250 Bonus";
    
    if (current >= 180000) {
      return { progress: 100, target: 180000, current, tierName: "Max Tier Achieved!" };
    } else if (current >= 150000) {
      target = 180000;
      tierName = "£1,000 Bonus";
    } else if (current >= 120000) {
      target = 150000;
      tierName = "£500 Bonus";
    }
    
    const progress = (current / target) * 100;
    return { progress: Math.min(progress, 100), target, current, tierName };
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-800"></div>
      </div>
    );
  }

  const progressData = getProgressToNextTier();

  return (
    <div className="min-h-screen bg-slate-50" data-testid="commission-dashboard">
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Performance & Commission</h1>
            <p className="text-slate-500">Track your activity, conversions, and earnings</p>
          </div>
          
          {/* Period Selectors */}
          <div className="flex items-center gap-3">
            <Select value={selectedPeriod} onValueChange={setSelectedPeriod}>
              <SelectTrigger className="w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PERIODS.map(p => (
                  <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={String(selectedYear)} onValueChange={(v) => setSelectedYear(parseInt(v))}>
              <SelectTrigger className="w-28">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {[2024, 2025, 2026].map(year => (
                  <SelectItem key={year} value={String(year)}>{year}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Select value={String(selectedMonth)} onValueChange={(v) => setSelectedMonth(parseInt(v))}>
              <SelectTrigger className="w-36">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {MONTHS.map((month, i) => (
                  <SelectItem key={i} value={String(i + 1)}>{month}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Main Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-6">
          <TabsList className="mb-4">
            {isDirector && (
              <TabsTrigger value="team-overview" className="flex items-center gap-2">
                <Users className="h-4 w-4" />
                Team Overview
              </TabsTrigger>
            )}
            <TabsTrigger value="performance" className="flex items-center gap-2">
              <BarChart3 className="h-4 w-4" />
              Performance
            </TabsTrigger>
            <TabsTrigger value="commission" className="flex items-center gap-2">
              <PoundSterling className="h-4 w-4" />
              Commission
            </TabsTrigger>
          </TabsList>

          {/* Director Team Overview Tab */}
          {isDirector && (
            <TabsContent value="team-overview">
              {teamOverview ? (
                <>
                  {/* Summary Cards */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                    <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
                      <CardContent className="p-4">
                        <div className="text-blue-100 text-sm">Monthly Fees</div>
                        <div className="text-2xl font-bold">{formatCurrency(teamOverview.summary?.total_monthly_fees)}</div>
                        <div className="text-blue-200 text-xs mt-1">{teamOverview.summary?.total_deals} deals</div>
                      </CardContent>
                    </Card>
                    <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white">
                      <CardContent className="p-4">
                        <div className="text-green-100 text-sm">Commission Pot (7%)</div>
                        <div className="text-2xl font-bold">{formatCurrency(teamOverview.summary?.pot_value_7_percent)}</div>
                        <div className="text-green-200 text-xs mt-1">{formatCurrency(teamOverview.summary?.per_person_share)} per person</div>
                      </CardContent>
                    </Card>
                    <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white">
                      <CardContent className="p-4">
                        <div className="text-purple-100 text-sm">Total Commission Payout</div>
                        <div className="text-2xl font-bold">{formatCurrency(teamOverview.summary?.total_commission_payout)}</div>
                        <div className="text-purple-200 text-xs mt-1">{teamOverview.summary?.total_staff} staff members</div>
                      </CardContent>
                    </Card>
                    <Card className="bg-gradient-to-br from-orange-500 to-orange-600 text-white">
                      <CardContent className="p-4">
                        <div className="text-orange-100 text-sm">Quarterly Bonus</div>
                        <div className="text-2xl font-bold">{formatCurrency(teamOverview.summary?.quarterly_bonus_tier)}</div>
                        <div className="text-orange-200 text-xs mt-1">Q{teamOverview.period?.quarter} • {formatCurrency(teamOverview.summary?.total_quarterly_fees)} YTD</div>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Staff Earnings Table */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Users className="h-5 w-5" />
                        All Staff Earnings - {MONTHS[selectedMonth - 1]} {selectedYear}
                      </CardTitle>
                      <CardDescription>Complete breakdown of all team member earnings</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead>
                            <tr className="border-b bg-slate-50">
                              <th className="text-left p-3 font-medium text-slate-700">Staff Member</th>
                              <th className="text-left p-3 font-medium text-slate-700">Role</th>
                              <th className="text-center p-3 font-medium text-slate-700">Deals</th>
                              <th className="text-right p-3 font-medium text-slate-700">Fees Generated</th>
                              <th className="text-center p-3 font-medium text-slate-700">Referrals</th>
                              <th className="text-right p-3 font-medium text-slate-700">Commission</th>
                            </tr>
                          </thead>
                          <tbody>
                            {teamOverview.staff?.map((member, idx) => (
                              <tr key={member.user_id || idx} className="border-b hover:bg-slate-50">
                                <td className="p-3">
                                  <div className="font-medium text-slate-900">{member.name}</div>
                                  <div className="text-xs text-slate-500">{member.email}</div>
                                </td>
                                <td className="p-3">
                                  <Badge variant="outline" className="text-xs">
                                    {member.role_display}
                                  </Badge>
                                </td>
                                <td className="p-3 text-center">
                                  <span className="font-medium">{member.deals_count}</span>
                                </td>
                                <td className="p-3 text-right">
                                  <span className="font-medium text-slate-700">{formatCurrency(member.fees_generated)}</span>
                                </td>
                                <td className="p-3 text-center">
                                  <span className="text-slate-600">{member.referrals_count}</span>
                                </td>
                                <td className="p-3 text-right">
                                  <div className="font-bold text-green-600">{formatCurrency(member.total_commission)}</div>
                                  {member.commission_breakdown && (
                                    <div className="text-xs text-slate-400">
                                      {member.commission_breakdown.base_share > 0 && `Base: ${formatCurrency(member.commission_breakdown.base_share)}`}
                                      {member.commission_breakdown.referrals > 0 && ` + Ref: ${formatCurrency(member.commission_breakdown.referrals)}`}
                                      {member.commission_breakdown.quarterly_bonus > 0 && ` + Bonus: ${formatCurrency(member.commission_breakdown.quarterly_bonus)}`}
                                      {member.commission_breakdown.exchange_commission > 0 && `Exch: ${formatCurrency(member.commission_breakdown.exchange_commission)}`}
                                    </div>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                          <tfoot>
                            <tr className="bg-slate-100 font-bold">
                              <td className="p-3" colSpan={2}>TOTAL</td>
                              <td className="p-3 text-center">{teamOverview.staff?.reduce((sum, m) => sum + m.deals_count, 0)}</td>
                              <td className="p-3 text-right">{formatCurrency(teamOverview.staff?.reduce((sum, m) => sum + m.fees_generated, 0))}</td>
                              <td className="p-3 text-center">{teamOverview.staff?.reduce((sum, m) => sum + m.referrals_count, 0)}</td>
                              <td className="p-3 text-right text-green-700">{formatCurrency(teamOverview.summary?.total_commission_payout)}</td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Recent Deals */}
                  {teamOverview.recent_deals?.length > 0 && (
                    <Card className="mt-6">
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                          <Home className="h-5 w-5" />
                          Recent Exchanged Deals
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="divide-y">
                          {teamOverview.recent_deals.map((deal) => (
                            <div 
                              key={deal.case_id}
                              className="py-3 flex items-center justify-between hover:bg-slate-50 cursor-pointer rounded px-2"
                              onClick={() => navigate(`/sales/case/${deal.case_id}`)}
                            >
                              <div>
                                <div className="font-medium">{deal.address}</div>
                                <div className="text-sm text-slate-500">
                                  {deal.exchange_date ? format(new Date(deal.exchange_date), "d MMM yyyy") : "—"}
                                </div>
                              </div>
                              <div className="font-bold text-green-600">
                                {formatCurrency(deal.selling_fee)}
                              </div>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </>
              ) : (
                <Card>
                  <CardContent className="p-8 text-center text-slate-500">
                    Loading team overview...
                  </CardContent>
                </Card>
              )}
            </TabsContent>
          )}

          {/* Performance Tab */}
          <TabsContent value="performance">
            {/* Sales Progressor View - Simplified */}
            {isSalesProgressor ? (
              <Card className="mb-6">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <BarChart3 className="h-5 w-5" />
                    Sales Progression Activity - {PERIODS.find(p => p.value === selectedPeriod)?.label}
                  </CardTitle>
                  <CardDescription>Your progression metrics</CardDescription>
                </CardHeader>
                <CardContent>
                  {/* Activity Metrics - Only relevant ones for Sales Progressor */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                    <MetricCard icon={CheckCircle2} label="Exchanges" value={myPerformance?.metrics?.exchanges || 0} color="text-green-600" />
                    <MetricCard icon={Trophy} label="Completions" value={myPerformance?.metrics?.completions || 0} color="text-yellow-600" />
                    <MetricCard icon={Briefcase} label="Solicitor Leads" value={myPerformance?.metrics?.solicitor_referrals || 0} color="text-indigo-600" />
                    <MetricCard icon={PoundSterling} label="Mortgage Appts" value={myPerformance?.metrics?.mortgage_referrals || 0} color="text-pink-600" />
                  </div>

                  {/* Single Conversion Rate - Sales Memo to Completion */}
                  <div className="border-t pt-4">
                    <h4 className="font-medium text-slate-700 mb-3 flex items-center gap-2">
                      <Percent className="h-4 w-4" />
                      Conversion Rate
                    </h4>
                    <div className="max-w-xs">
                      <ConversionCard 
                        icon={TrendingUp} 
                        label="Sales Memo → Completion" 
                        value={myPerformance?.conversions?.instruction_to_completion || 0} 
                      />
                    </div>
                  </div>

                  {/* Total Exchanged Value */}
                  {myPerformance?.metrics?.total_fees > 0 && (
                    <div className="border-t pt-4 mt-4">
                      <div className="flex items-center justify-between p-4 bg-green-50 rounded-lg">
                        <div>
                          <div className="text-sm text-green-600">Total Exchanged Value This Month</div>
                          <div className="text-2xl font-bold text-green-700">
                            {formatCurrency(myPerformance.metrics?.total_fees)}
                          </div>
                        </div>
                        <PoundSterling className="h-8 w-8 text-green-500" />
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            ) : (
              /* Standard Performance View for other roles */
              myPerformance && (
                <>
                  <Card className="mb-6">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <BarChart3 className="h-5 w-5" />
                        My Activity - {PERIODS.find(p => p.value === selectedPeriod)?.label}
                      </CardTitle>
                      <CardDescription>Your performance metrics and conversion rates</CardDescription>
                    </CardHeader>
                    <CardContent>
                      {/* Activity Metrics */}
                      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4 mb-6">
                        <MetricCard icon={ClipboardCheck} label="Valuations" value={myPerformance.metrics?.valuations || 0} color="text-purple-600" />
                        <MetricCard icon={FileText} label="Instructions" value={myPerformance.metrics?.instructions || 0} color="text-blue-600" />
                        <MetricCard icon={Eye} label="Viewings" value={myPerformance.metrics?.viewings_booked || 0} color="text-cyan-600" />
                        <MetricCard icon={Handshake} label="Sales Agreed" value={myPerformance.metrics?.sales_agreed || 0} color="text-orange-600" />
                        <MetricCard icon={CheckCircle2} label="Exchanges" value={myPerformance.metrics?.exchanges || 0} color="text-green-600" />
                        <MetricCard icon={Trophy} label="Completions" value={myPerformance.metrics?.completions || 0} color="text-yellow-600" />
                        <MetricCard icon={Briefcase} label="Solicitor Refs" value={myPerformance.metrics?.solicitor_referrals || 0} color="text-indigo-600" />
                        <MetricCard icon={PoundSterling} label="Mortgage Refs" value={myPerformance.metrics?.mortgage_referrals || 0} color="text-pink-600" />
                      </div>

                      {/* Conversion Rates */}
                      <div className="border-t pt-4">
                        <h4 className="font-medium text-slate-700 mb-3 flex items-center gap-2">
                          <Percent className="h-4 w-4" />
                          Conversion Rates
                        </h4>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                          <ConversionCard 
                            icon={TrendingUp} 
                            label="Valuation → Instruction" 
                            value={myPerformance.conversions?.valuation_to_instruction || 0} 
                          />
                          <ConversionCard 
                            icon={Eye} 
                            label="Viewings → Sale" 
                            value={myPerformance.conversions?.viewings_to_sale || 0} 
                          />
                          <ConversionCard 
                            icon={Handshake} 
                            label="Instruction → Exchange" 
                            value={myPerformance.conversions?.instruction_to_exchange || 0} 
                          />
                          <ConversionCard 
                            icon={CheckCircle2} 
                            label="Instruction → Completion" 
                            value={myPerformance.conversions?.instruction_to_completion || 0} 
                          />
                        </div>
                      </div>

                      {/* Total Fees */}
                      {myPerformance.metrics?.total_fees > 0 && (
                        <div className="border-t pt-4 mt-4">
                          <div className="flex items-center justify-between p-4 bg-green-50 rounded-lg">
                            <div>
                              <div className="text-sm text-green-600">Total Exchanged Fees (Your Deals)</div>
                              <div className="text-2xl font-bold text-green-700">
                                {formatCurrency(myPerformance.metrics?.total_fees)}
                              </div>
                            </div>
                            <PoundSterling className="h-8 w-8 text-green-500" />
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </>
              )
            )}

            {/* Director View - All Staff Performance by Department */}
            {isDirector && allPerformance && (
              <>
                {/* Sales Team Performance */}
                {allPerformance.sales_team?.length > 0 && (
                  <Card className="mb-6">
                    <CardHeader className="bg-blue-50">
                      <CardTitle className="flex items-center gap-2 text-blue-800">
                        <Home className="h-5 w-5" />
                        Sales Team Performance
                      </CardTitle>
                      <CardDescription>Lauren, Charis, Saurav, Roop - Full sales metrics</CardDescription>
                    </CardHeader>
                    <CardContent className="pt-4">
                      {/* Sales Team Totals */}
                      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3 mb-6 p-4 bg-slate-50 rounded-lg">
                        <div className="text-center">
                          <div className="text-2xl font-bold text-purple-600">{allPerformance.sales_totals?.valuations || 0}</div>
                          <div className="text-xs text-slate-500">Valuations</div>
                        </div>
                        <div className="text-center">
                          <div className="text-2xl font-bold text-blue-600">{allPerformance.sales_totals?.instructions || 0}</div>
                          <div className="text-xs text-slate-500">Instructions</div>
                        </div>
                        <div className="text-center">
                          <div className="text-2xl font-bold text-cyan-600">{allPerformance.sales_totals?.viewings_booked || 0}</div>
                          <div className="text-xs text-slate-500">Viewings</div>
                        </div>
                        <div className="text-center">
                          <div className="text-2xl font-bold text-orange-600">{allPerformance.sales_totals?.sales_agreed || 0}</div>
                          <div className="text-xs text-slate-500">Sales Agreed</div>
                        </div>
                        <div className="text-center">
                          <div className="text-2xl font-bold text-green-600">{allPerformance.sales_totals?.exchanges || 0}</div>
                          <div className="text-xs text-slate-500">Exchanges</div>
                        </div>
                        <div className="text-center">
                          <div className="text-2xl font-bold text-yellow-600">{allPerformance.sales_totals?.completions || 0}</div>
                          <div className="text-xs text-slate-500">Completions</div>
                        </div>
                        <div className="text-center">
                          <div className="text-2xl font-bold text-indigo-600">{allPerformance.sales_totals?.solicitor_referrals || 0}</div>
                          <div className="text-xs text-slate-500">Solicitor Refs</div>
                        </div>
                        <div className="text-center">
                          <div className="text-2xl font-bold text-pink-600">{allPerformance.sales_totals?.mortgage_referrals || 0}</div>
                          <div className="text-xs text-slate-500">Mortgage Refs</div>
                        </div>
                      </div>

                      {/* Individual Sales Staff */}
                      <div className="space-y-3">
                        {allPerformance.sales_team.map((staff) => (
                          <div key={staff.user_id} className="p-4 bg-white border rounded-lg hover:border-blue-200 transition-colors">
                            <div className="flex items-center justify-between mb-3">
                              <div>
                                <div className="font-medium text-slate-900">{staff.name}</div>
                                <div className="text-xs text-slate-400">{staff.role_display}</div>
                              </div>
                              <div className="text-sm font-semibold text-green-600">
                                {formatCurrency(staff.metrics?.fees_generated)} in fees
                              </div>
                            </div>
                            <div className="grid grid-cols-4 md:grid-cols-8 gap-2 text-center text-sm">
                              <div><div className="font-bold text-purple-600">{staff.metrics?.valuations || 0}</div><div className="text-xs text-slate-400">Vals</div></div>
                              <div><div className="font-bold text-blue-600">{staff.metrics?.instructions || 0}</div><div className="text-xs text-slate-400">Instr</div></div>
                              <div><div className="font-bold text-cyan-600">{staff.metrics?.viewings_booked || 0}</div><div className="text-xs text-slate-400">Views</div></div>
                              <div><div className="font-bold text-orange-600">{staff.metrics?.sales_agreed || 0}</div><div className="text-xs text-slate-400">Agreed</div></div>
                              <div><div className="font-bold text-green-600">{staff.metrics?.exchanges || 0}</div><div className="text-xs text-slate-400">Exch</div></div>
                              <div><div className="font-bold text-yellow-600">{staff.metrics?.completions || 0}</div><div className="text-xs text-slate-400">Comp</div></div>
                              <div><div className="font-bold text-indigo-600">{staff.metrics?.solicitor_referrals || 0}</div><div className="text-xs text-slate-400">Sol</div></div>
                              <div><div className="font-bold text-pink-600">{staff.metrics?.mortgage_referrals || 0}</div><div className="text-xs text-slate-400">Mort</div></div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Progression Team Performance */}
                {allPerformance.progression_team?.length > 0 && (
                  <Card className="mb-6">
                    <CardHeader className="bg-green-50">
                      <CardTitle className="flex items-center gap-2 text-green-800">
                        <TrendingUp className="h-5 w-5" />
                        Sales Progression Performance
                      </CardTitle>
                      <CardDescription>Tracey - Exchanges, Completions, Fall-throughs, Solicitors</CardDescription>
                    </CardHeader>
                    <CardContent className="pt-4">
                      {/* Progression Team Totals */}
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6 p-4 bg-slate-50 rounded-lg">
                        <div className="text-center">
                          <div className="text-2xl font-bold text-green-600">{allPerformance.progression_totals?.exchanges || 0}</div>
                          <div className="text-xs text-slate-500">Exchanges</div>
                        </div>
                        <div className="text-center">
                          <div className="text-2xl font-bold text-yellow-600">{allPerformance.progression_totals?.completions || 0}</div>
                          <div className="text-xs text-slate-500">Completions</div>
                        </div>
                        <div className="text-center">
                          <div className="text-2xl font-bold text-red-600">{allPerformance.progression_totals?.fall_throughs || 0}</div>
                          <div className="text-xs text-slate-500">Fall-throughs</div>
                        </div>
                        <div className="text-center">
                          <div className="text-2xl font-bold text-indigo-600">{allPerformance.progression_totals?.solicitor_referrals || 0}</div>
                          <div className="text-xs text-slate-500">Solicitor Refs</div>
                        </div>
                      </div>

                      {/* Individual Progression Staff */}
                      <div className="space-y-3">
                        {allPerformance.progression_team.map((staff) => (
                          <div key={staff.user_id} className="p-4 bg-white border rounded-lg hover:border-green-200 transition-colors">
                            <div className="flex items-center justify-between mb-3">
                              <div>
                                <div className="font-medium text-slate-900">{staff.name}</div>
                                <div className="text-xs text-slate-400">{staff.role_display}</div>
                              </div>
                              <div className="text-sm font-semibold text-green-600">
                                {formatCurrency(staff.metrics?.fees_processed)} processed
                              </div>
                            </div>
                            <div className="grid grid-cols-4 gap-4 text-center text-sm">
                              <div><div className="font-bold text-green-600">{staff.metrics?.exchanges || 0}</div><div className="text-xs text-slate-400">Exchanges</div></div>
                              <div><div className="font-bold text-yellow-600">{staff.metrics?.completions || 0}</div><div className="text-xs text-slate-400">Completions</div></div>
                              <div><div className="font-bold text-red-600">{staff.metrics?.fall_throughs || 0}</div><div className="text-xs text-slate-400">Fall-throughs</div></div>
                              <div><div className="font-bold text-indigo-600">{staff.metrics?.solicitor_referrals || 0}</div><div className="text-xs text-slate-400">Solicitor Refs</div></div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Lettings Team Performance */}
                {allPerformance.lettings_team?.length > 0 && (
                  <Card className="mb-6">
                    <CardHeader className="bg-purple-50">
                      <CardTitle className="flex items-center gap-2 text-purple-800">
                        <Briefcase className="h-5 w-5" />
                        Lettings Team Performance
                      </CardTitle>
                      <CardDescription>Jack Birch, Owen Franklin - Tenancy metrics</CardDescription>
                    </CardHeader>
                    <CardContent className="pt-4">
                      {/* Lettings Team Totals */}
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6 p-4 bg-slate-50 rounded-lg">
                        <div className="text-center">
                          <div className="text-2xl font-bold text-purple-600">{allPerformance.lettings_totals?.new_tenancies || 0}</div>
                          <div className="text-xs text-slate-500">New Tenancies</div>
                        </div>
                        <div className="text-center">
                          <div className="text-2xl font-bold text-blue-600">{allPerformance.lettings_totals?.renewals || 0}</div>
                          <div className="text-xs text-slate-500">Renewals</div>
                        </div>
                        <div className="text-center">
                          <div className="text-2xl font-bold text-orange-600">{allPerformance.lettings_totals?.checkouts || 0}</div>
                          <div className="text-xs text-slate-500">Checkouts</div>
                        </div>
                        <div className="text-center">
                          <div className="text-2xl font-bold text-cyan-600">{allPerformance.lettings_totals?.viewings || 0}</div>
                          <div className="text-xs text-slate-500">Viewings</div>
                        </div>
                      </div>

                      {/* Individual Lettings Staff */}
                      <div className="space-y-3">
                        {allPerformance.lettings_team.map((staff) => (
                          <div key={staff.user_id} className="p-4 bg-white border rounded-lg hover:border-purple-200 transition-colors">
                            <div className="flex items-center justify-between mb-3">
                              <div>
                                <div className="font-medium text-slate-900">{staff.name}</div>
                                <div className="text-xs text-slate-400">{staff.role_display}</div>
                              </div>
                            </div>
                            <div className="grid grid-cols-4 gap-4 text-center text-sm">
                              <div><div className="font-bold text-purple-600">{staff.metrics?.new_tenancies || 0}</div><div className="text-xs text-slate-400">New</div></div>
                              <div><div className="font-bold text-blue-600">{staff.metrics?.renewals || 0}</div><div className="text-xs text-slate-400">Renewals</div></div>
                              <div><div className="font-bold text-orange-600">{staff.metrics?.checkouts || 0}</div><div className="text-xs text-slate-400">Checkouts</div></div>
                              <div><div className="font-bold text-cyan-600">{staff.metrics?.viewings || 0}</div><div className="text-xs text-slate-400">Viewings</div></div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </>
            )}
          </TabsContent>

          {/* Commission Tab */}
          <TabsContent value="commission">
        {/* Sales Progressor Commission View - Tracey's Structure */}
        {isSalesProgressor ? (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <PoundSterling className="h-5 w-5" />
                Your Commission Structure
              </CardTitle>
              <CardDescription>Sales Progressor commission rates</CardDescription>
            </CardHeader>
            <CardContent>
              {/* Commission Structure Display */}
              <div className="grid md:grid-cols-2 gap-6 mb-6">
                {/* Exchange Value Commission */}
                <div className="p-4 bg-gradient-to-br from-emerald-50 to-green-50 rounded-lg border border-emerald-200">
                  <h3 className="font-semibold text-emerald-800 mb-3">Monthly Exchange Value Commission</h3>
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-slate-600">Up to £50,000</span>
                      <Badge className="bg-emerald-500">1%</Badge>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-slate-600">Above £50,000</span>
                      <Badge className="bg-emerald-600">2%</Badge>
                    </div>
                  </div>
                </div>

                {/* Fixed Referral Fees */}
                <div className="p-4 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg border border-blue-200">
                  <h3 className="font-semibold text-blue-800 mb-3">Referral Fees</h3>
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-slate-600">H&E Removal Quote</span>
                      <Badge className="bg-blue-500">£100</Badge>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-slate-600">Solicitor Lead</span>
                      <Badge className="bg-blue-500">£25</Badge>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-slate-600">Mortgage Appointment</span>
                      <Badge className="bg-blue-500">£10</Badge>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-slate-600">Mortgage Sign-up</span>
                      <Badge className="bg-blue-500">£15</Badge>
                    </div>
                  </div>
                </div>
              </div>

              {/* Current Month Summary */}
              <div className="border-t pt-6">
                <h3 className="font-semibold text-slate-700 mb-4">This Month&apos;s Summary</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="p-4 bg-slate-50 rounded-lg text-center">
                    <div className="text-2xl font-bold text-emerald-600">
                      {formatCurrency(myCommission?.monthly?.total_branch_fees || 0)}
                    </div>
                    <div className="text-xs text-slate-500">Exchange Value</div>
                  </div>
                  <div className="p-4 bg-slate-50 rounded-lg text-center">
                    <div className="text-2xl font-bold text-blue-600">
                      {myPerformance?.metrics?.solicitor_referrals || 0}
                    </div>
                    <div className="text-xs text-slate-500">Solicitor Leads</div>
                  </div>
                  <div className="p-4 bg-slate-50 rounded-lg text-center">
                    <div className="text-2xl font-bold text-purple-600">
                      {myPerformance?.metrics?.mortgage_referrals || 0}
                    </div>
                    <div className="text-xs text-slate-500">Mortgage Refs</div>
                  </div>
                  <div className="p-4 bg-green-100 rounded-lg text-center">
                    <div className="text-2xl font-bold text-green-700">
                      {formatCurrency(myCommission?.total_commission || 0)}
                    </div>
                    <div className="text-xs text-green-600">Total Commission</div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        ) : (
          /* Standard Commission View for other roles */
          myCommission && (
          <>
            {/* Top Stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
              <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-green-100 text-sm">Your Monthly Commission</p>
                      <p className="text-3xl font-bold mt-1">
                        {formatCurrency(myCommission.total_commission)}
                      </p>
                    </div>
                    <PoundSterling className="h-10 w-10 text-green-200" />
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-slate-500 text-sm">Your Share (7% Pot)</p>
                      <p className="text-2xl font-bold text-slate-900">
                        {formatCurrency(myCommission.monthly?.your_share)}
                      </p>
                    </div>
                    <Users className="h-8 w-8 text-blue-500" />
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-slate-500 text-sm">Referral Earnings</p>
                      <p className="text-2xl font-bold text-slate-900">
                        {formatCurrency(myCommission.referrals?.total)}
                      </p>
                    </div>
                    <Briefcase className="h-8 w-8 text-purple-500" />
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-slate-500 text-sm">Your Deals This Month</p>
                      <p className="text-2xl font-bold text-slate-900">
                        {myCommission.monthly?.your_deals || 0}
                      </p>
                    </div>
                    <Home className="h-8 w-8 text-orange-500" />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Monthly & Quarterly Details */}
            <div className="grid lg:grid-cols-2 gap-6 mb-6">
              {/* Monthly Breakdown */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Calendar className="h-5 w-5" />
                    {myCommission.month_name} {myCommission.year} Breakdown
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex justify-between items-center py-2 border-b">
                    <span className="text-slate-600">Branch Total Exchanged Fees</span>
                    <span className="font-semibold">{formatCurrency(myCommission.monthly?.total_branch_fees)}</span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b">
                    <span className="text-slate-600">7% Shared Pot</span>
                    <span className="font-semibold">{formatCurrency(myCommission.monthly?.pot_value_7_percent)}</span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b bg-blue-50 px-2 rounded">
                    <span className="text-blue-700 font-medium">Your Share</span>
                    <span className="font-bold text-blue-700">{formatCurrency(myCommission.monthly?.your_share)}</span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b">
                    <span className="text-slate-600">Your Fee Contribution</span>
                    <span className="font-semibold">{formatCurrency(myCommission.monthly?.your_fee_contribution)}</span>
                  </div>
                  <div className="flex justify-between items-center py-2">
                    <span className="text-slate-600">Total Branch Deals</span>
                    <span className="font-semibold">{myCommission.monthly?.total_deals}</span>
                  </div>
                </CardContent>
              </Card>

              {/* Quarterly Progress */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Trophy className="h-5 w-5 text-yellow-500" />
                    Q{myCommission.quarterly?.quarter} Performance Bonus
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="text-center py-4">
                    <div className="text-3xl font-bold text-slate-900">
                      {formatCurrency(myCommission.quarterly?.total_fees_so_far)}
                    </div>
                    <p className="text-slate-500 text-sm">Total Exchanged Fees This Quarter</p>
                  </div>
                  
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span>Progress to next tier</span>
                      <span className="font-medium">{myCommission.quarterly?.bonus_tier}</span>
                    </div>
                    <Progress value={progressData.progress} className="h-3" />
                    <div className="flex justify-between text-xs text-slate-500">
                      <span>£0</span>
                      <span>£120k</span>
                      <span>£150k</span>
                      <span>£180k+</span>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-3 gap-2 pt-4">
                    <div className={`p-3 rounded-lg text-center ${myCommission.quarterly?.bonus_amount >= 250 ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-400"}`}>
                      <div className="font-bold">£250</div>
                      <div className="text-xs">£120k+</div>
                    </div>
                    <div className={`p-3 rounded-lg text-center ${myCommission.quarterly?.bonus_amount >= 500 ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-400"}`}>
                      <div className="font-bold">£500</div>
                      <div className="text-xs">£150k+</div>
                    </div>
                    <div className={`p-3 rounded-lg text-center ${myCommission.quarterly?.bonus_amount >= 1000 ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-400"}`}>
                      <div className="font-bold">£1,000</div>
                      <div className="text-xs">£180k+</div>
                    </div>
                  </div>
                  
                  {myCommission.quarterly?.is_top_performer && (
                    <div className="flex items-center gap-2 p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                      <Crown className="h-5 w-5 text-yellow-600" />
                      <div>
                        <p className="font-medium text-yellow-800">Top Performer!</p>
                        <p className="text-sm text-yellow-600">You&apos;ve earned the £500 bonus this quarter</p>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Referrals Breakdown */}
            <Card className="mb-6">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Briefcase className="h-5 w-5" />
                  Referral Earnings
                </CardTitle>
                <CardDescription>Additional commission from referrals this month</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="p-4 bg-slate-50 rounded-lg text-center">
                    <Home className="h-6 w-6 mx-auto mb-2 text-blue-500" />
                    <div className="font-bold">{formatCurrency(myCommission.referrals?.breakdown?.valuation)}</div>
                    <div className="text-xs text-slate-500">Valuations</div>
                  </div>
                  <div className="p-4 bg-slate-50 rounded-lg text-center">
                    <PoundSterling className="h-6 w-6 mx-auto mb-2 text-green-500" />
                    <div className="font-bold">{formatCurrency(myCommission.referrals?.breakdown?.mortgage)}</div>
                    <div className="text-xs text-slate-500">Mortgages</div>
                  </div>
                  <div className="p-4 bg-slate-50 rounded-lg text-center">
                    <FileText className="h-6 w-6 mx-auto mb-2 text-purple-500" />
                    <div className="font-bold">{formatCurrency(myCommission.referrals?.breakdown?.solicitor)}</div>
                    <div className="text-xs text-slate-500">Solicitors</div>
                  </div>
                  <div className="p-4 bg-slate-50 rounded-lg text-center">
                    <Truck className="h-6 w-6 mx-auto mb-2 text-orange-500" />
                    <div className="font-bold">{formatCurrency(myCommission.referrals?.breakdown?.removals)}</div>
                    <div className="text-xs text-slate-500">Removals</div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* My Deals This Month */}
            {myCommission.my_deals?.length > 0 && (
              <Card className="mb-6">
                <CardHeader>
                  <CardTitle>Your Exchanged Deals This Month</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="divide-y">
                    {myCommission.my_deals.map((deal) => (
                      <div 
                        key={deal.case_id}
                        className="py-3 flex items-center justify-between hover:bg-slate-50 cursor-pointer"
                        onClick={() => navigate(`/sales/case/${deal.case_id}`)}
                      >
                        <div>
                          <div className="font-medium">{deal.address}</div>
                          <div className="text-sm text-slate-500">
                            Exchanged: {deal.exchange_date ? format(new Date(deal.exchange_date), "d MMM yyyy") : "—"}
                          </div>
                        </div>
                        <div className="font-bold text-green-600">
                          {formatCurrency(deal.selling_fee)}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </>
          )
        )}

        {/* Director View - All Staff Commissions */}
        {isDirector && allCommissions && (
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Users className="h-5 w-5" />
                    Team Commission Overview
                  </CardTitle>
                  <CardDescription>Q{allCommissions.quarter} {allCommissions.year}</CardDescription>
                </div>
                <Select value={String(selectedQuarter)} onValueChange={(v) => setSelectedQuarter(parseInt(v))}>
                  <SelectTrigger className="w-40">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {QUARTERS.map(q => (
                      <SelectItem key={q.value} value={String(q.value)}>{q.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>
            <CardContent>
              {/* Summary Stats */}
              <div className="grid grid-cols-4 gap-4 mb-6">
                <div className="p-4 bg-blue-50 rounded-lg">
                  <div className="text-sm text-blue-600">Total Exchanged Fees</div>
                  <div className="text-2xl font-bold text-blue-700">
                    {formatCurrency(allCommissions.summary?.total_exchanged_fees)}
                  </div>
                </div>
                <div className="p-4 bg-green-50 rounded-lg">
                  <div className="text-sm text-green-600">7% Pot Value</div>
                  <div className="text-2xl font-bold text-green-700">
                    {formatCurrency(allCommissions.summary?.pot_value_7_percent)}
                  </div>
                </div>
                <div className="p-4 bg-purple-50 rounded-lg">
                  <div className="text-sm text-purple-600">Per Person Share</div>
                  <div className="text-2xl font-bold text-purple-700">
                    {formatCurrency(allCommissions.summary?.per_person_share)}
                  </div>
                </div>
                <div className="p-4 bg-orange-50 rounded-lg">
                  <div className="text-sm text-orange-600">Quarterly Bonus Tier</div>
                  <div className="text-2xl font-bold text-orange-700">
                    {formatCurrency(allCommissions.summary?.quarterly_bonus_tier)}
                  </div>
                </div>
              </div>
              
              {/* Negotiator Breakdown */}
              <div className="space-y-3">
                {allCommissions.negotiators?.map((neg) => (
                  <div 
                    key={neg.staff_id}
                    className={`p-4 rounded-lg border ${neg.is_top_performer ? "border-yellow-300 bg-yellow-50" : "bg-white"}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        {neg.is_top_performer && <Crown className="h-5 w-5 text-yellow-500" />}
                        <div>
                          <div className="font-medium flex items-center gap-2">
                            {neg.name}
                            {neg.is_top_performer && (
                              <Badge className="bg-yellow-500">Top Performer</Badge>
                            )}
                          </div>
                          <div className="text-sm text-slate-500">
                            {neg.deal_count} deals • {formatCurrency(neg.fee_contribution)} contribution
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-xl font-bold text-green-600">
                          {formatCurrency(neg.total_commission)}
                        </div>
                        <div className="text-xs text-slate-500">
                          Base: {formatCurrency(neg.base_share)} + Bonus: {formatCurrency(neg.quarterly_bonus + neg.top_performer_bonus)} + Referrals: {formatCurrency(neg.referral_earnings)}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
